import React from 'react';
import './AvatarPicker.css';

export const AVATAR_OPTIONS: { id: string; label: string; description: string }[] = [
  { id: 'fox', label: 'The Fox', description: 'Clever and curious, always ready for a new tale' },
  { id: 'owl', label: 'The Owl', description: 'Wise and watchful, a guardian of bedtime stories' },
  { id: 'bunny', label: 'The Bunny', description: 'Quick and gentle, hopping from adventure to adventure' },
  { id: 'bear', label: 'The Bear', description: 'Warm and brave, protector of the storybook woods' },
  { id: 'deer', label: 'The Deer', description: 'Graceful and kind, a friend to every forest creature' },
  { id: 'hedgehog', label: 'The Hedgehog', description: 'Thoughtful and cozy, happiest curled up with a book' },
  { id: 'squirrel', label: 'The Squirrel', description: 'Playful and quick, collecting stories like acorns' },
  { id: 'raccoon', label: 'The Raccoon', description: 'Resourceful and mischievous, finding magic in unexpected places' },
  { id: 'wolf', label: 'The Wolf', description: 'Loyal and bold, leading the pack through every chapter' },
  { id: 'mouse', label: 'The Mouse', description: 'Small but mighty, proving great tales come in all sizes' },
  { id: 'badger', label: 'The Badger', description: 'Steadfast and sturdy, a dependable friend in every story' },
  { id: 'nightingale', label: 'The Nightingale', description: 'Spirited and graceful, singing stories on the wind' },
];

/** Map of legacy avatar IDs to their new equivalents */
const LEGACY_AVATAR_MAP: Record<string, string> = {
  rabbit: 'bunny',
  butterfly: 'badger',
  bird: 'nightingale',
};

/** Resolve legacy avatar IDs to current ones */
export function resolveAvatarId(id: string): string {
  return LEGACY_AVATAR_MAP[id] || id;
}

function avatarIconUrl(id: string): string {
  const resolved = resolveAvatarId(id);
  return `${process.env.PUBLIC_URL}/images/avatar-icons/${resolved}-icon.png`;
}

interface AvatarPickerProps {
  value: string;
  onChange: (avatarId: string) => void;
  size?: 'sm' | 'md';
}

export default function AvatarPicker({ value, onChange, size = 'md' }: AvatarPickerProps) {
  const resolvedValue = resolveAvatarId(value);

  return (
    <div className={`avatar-picker avatar-picker-${size}`}>
      {AVATAR_OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          className={`avatar-option ${resolvedValue === opt.id ? 'selected' : ''}`}
          onClick={() => onChange(opt.id)}
          title={opt.label}
          aria-label={opt.label}
        >
          <img
            src={avatarIconUrl(opt.id)}
            alt={opt.label}
            className="avatar-option-img"
            draggable={false}
          />
        </button>
      ))}
    </div>
  );
}

/** Render just the avatar icon by ID (for navbar, etc.) */
export function AvatarIcon({ avatarId, className }: { avatarId: string; className?: string }) {
  const resolved = resolveAvatarId(avatarId);
  const option = AVATAR_OPTIONS.find(o => o.id === resolved);
  if (!option) return null;
  return (
    <span className={`avatar-icon ${className || ''}`}>
      <img
        src={avatarIconUrl(resolved)}
        alt={option.label}
        className="avatar-icon-img"
        draggable={false}
      />
    </span>
  );
}
